// src/padplanner/planMapData.js
//
// Geometry helpers. First, what the dashboard's pins card draws, computed from
// what the server already answers. No points endpoint exists: /plans/perimeters is asked for the whole
// world (a bbox that passes its validation) and each outline becomes one pin at
// the center of its bounds. Capped at the route's 500, and the answer says
// when it was (truncated).

import area from "@turf/area";
import length from "@turf/length";

export const WORLD_BBOX = Object.freeze([-180, -90, 180, 90]);

function extend(b, c) {
  if (typeof c[0] === "number") {
    b[0] = Math.min(b[0], c[0]); b[1] = Math.min(b[1], c[1]);
    b[2] = Math.max(b[2], c[0]); b[3] = Math.max(b[3], c[1]);
  } else c.forEach((x) => extend(b, x));
}

/** [w, s, e, n] of a geometry's coordinates, or null for none. */
// A camera move the user asked for: map.fitBounds(b, opts, USER_ACTION).
// PadPlannerMap counts it as the user's move (its THE CAMERA section).
export const USER_ACTION_FLAG = "ppUserAction";
export const USER_ACTION = Object.freeze({ [USER_ACTION_FLAG]: true });

export function boundsOf(geometry) {
  if (!geometry?.coordinates) return null;
  const b = [Infinity, Infinity, -Infinity, -Infinity];
  extend(b, geometry.coordinates);
  return b.every(Number.isFinite) ? b : null;
}

/** One Point per outline, at its bounds' center, carrying id, plan_number, title and status. */
export function pinsFromPerimeters(fc) {
  const features = [];
  for (const f of fc?.features ?? []) {
    const b = boundsOf(f.geometry);
    if (!b) continue;
    features.push({
      type: "Feature", id: f.id,
      geometry: { type: "Point", coordinates: [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2] },
      properties: { id: f.properties?.id ?? f.id, plan_number: f.properties?.plan_number, title: f.properties?.title, status: f.properties?.status },
    });
  }
  return { type: "FeatureCollection", features };
}

/** The bounds of every pin, for fitting the map; null when there are none. */
export function pinsBounds(pins) {
  const pts = (pins?.features ?? []).map((f) => f.geometry.coordinates);
  if (pts.length === 0) return null;
  return boundsOf({ type: "MultiPoint", coordinates: pts });
}

// ── an unsaved ring, measured in the browser ──────────────────────────────
//
// New plan shows a drawn outline's area and perimeter before anything is
// saved. turf measures on a sphere: for a pad, well under a percent from the
// server's ST_Area(geography), which is the figure the plan keeps once saved.
// portal/portalMeasure.js has the same reasoning.

const M_PER_FOOT = 0.3048;

/** Square meters, or null for no (or a malformed) polygon. */
export function areaM2(geometry) {
  if (geometry?.type !== "Polygon" && geometry?.type !== "MultiPolygon") return null;
  try {
    const m2 = area({ type: "Feature", properties: {}, geometry });
    return Number.isFinite(m2) && m2 > 0 ? m2 : null;
  } catch {
    return null;
  }
}

/** "1,840 ft" around every ring, or null. */
export function formatPerimeterFt(geometry) {
  if (geometry?.type !== "Polygon" && geometry?.type !== "MultiPolygon") return null;
  try {
    const ft = (length({ type: "Feature", properties: {}, geometry }, { units: "kilometers" }) * 1000) / M_PER_FOOT;
    return Number.isFinite(ft) && ft > 0 ? `${Math.round(ft).toLocaleString("en-US")} ft` : null;
  } catch {
    return null;
  }
}

/**
 * The point an unsaved outline's imagery is LISTED at: its bounds' center.
 * That point can fall outside a concave outline, which costs nothing: listing
 * is advisory, and a pin is resolved by the server against the whole saved
 * perimeter, and can still be refused.
 */
export function listingPoint(geometry) {
  const b = boundsOf(geometry);
  return b ? [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2] : null;
}

/** A MultiPolygon of one part is a Polygon MlDraw can edit; more parts cannot be. */
export function editablePolygon(geometry) {
  if (!geometry) return null;
  if (geometry.type === "Polygon") return geometry;
  if (geometry.type === "MultiPolygon" && geometry.coordinates.length === 1) {
    return { type: "Polygon", coordinates: geometry.coordinates[0] };
  }
  return null;
}
